import { useEffect, useState } from "react";
import cx from "classnames";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { WithStringValues } from "common/utils/types";
import { Feature } from "common/types";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";

import Results from "client/common/components/results/Results";
import Pagination from "client/common/components/layouts/Pagination";
import PillButton from "client/common/components/buttons/PillButton";
import Body from "client/common/components/layouts/Body";
import { useProductsViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import { ProductListSource } from "client/analytics/types";
import { useFeatures } from "client/clinics/hooks";

import { useProductFilterOptionState, useProductSearchQuery } from "../hooks";
import { ProductSearchSearch, ProductSearchResultV2 as ProductSearchResultV2Type } from "../types";
import ProductSearchResult from "./ProductSearchResult";
import ProductSearchResultV2 from "./ProductSearchResultV2";
import ProductSearchFilterModal from "./ProductSearchFilterModal";
import { getProductFiltersFromSearch, getSearchString } from "../utils";
import ProductSearchFilterTags from "./ProductSearchFilterTags";

import styles from "../styles/ProductSearch.module.less";

type ProductSearchProps = {
  search: WithStringValues<ProductSearchSearch>;
  onUpdateSearch: (search: Partial<ProductSearchSearch>) => void;
};

const ProductSearch = (props: ProductSearchProps) => {
  const { search, onUpdateSearch } = props;

  const isV2 = useFeatures()[Feature.ProductSearchCardsV2];
  const SearchResultCard = isV2 ? ProductSearchResultV2 : ProductSearchResult;

  const [isFiltering, setIsFiltering] = useState(false);
  const { data: products, isLoading, error, isRefetching } = useProductSearchQuery(search);

  const { data: filterOptions } = useProductFilterOptionState();
  const sectionRef = useViewItemListOnVisible(products?.results, ProductListSource.SearchResults);
  const productsViewRef = useProductsViewOnVisible(
    products?.results,
    ProductListSource.SearchResults,
    search.q,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (isLoading) {
    return <Results.Loading description="Loading products" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="Product" />;
  }

  const filters = getProductFiltersFromSearch(search);

  return (
    <Body>
      <div className={styles.container}>
        <div className={styles.info}>
          <div className={styles.label}>
            {isRefetching ? <Spinner /> : getSearchString(products.paginator)}
          </div>
          <div className={styles.actions}>
            {filterOptions && (
              <ProductSearchFilterTags
                options={filterOptions.filters}
                value={filters}
                onChange={onUpdateSearch}
              />
            )}
            <PillButton
              variant="secondary"
              size="s"
              type="button"
              onClick={() => setIsFiltering(true)}
            >
              <span className={styles.filter}>
                <Icon icon={faFilter} /> Filter
              </span>
            </PillButton>
          </div>
        </div>
        <div>
          {filterOptions && (
            <ProductSearchFilterModal
              options={filterOptions.filters}
              isVisible={isFiltering}
              onClose={() => setIsFiltering(false)}
              value={filters}
              onChange={onUpdateSearch}
            />
          )}
          {products.results.length ? (
            <div ref={sectionRef} className={styles.search}>
              <div ref={productsViewRef} className={cx(styles.results, isV2 && styles.resultsV2)}>
                {/* When the v2 flag is on, the v2 endpoint populates the fields the v2 card reads. */}
                {products.results.map((result) => (
                  <SearchResultCard key={result.id} result={result as ProductSearchResultV2Type} />
                ))}
              </div>
              <Pagination
                paginator={products.paginator}
                onPageChange={(page) => onUpdateSearch({ page })}
                entityName="result"
              />
            </div>
          ) : (
            <Results.None description={`Could not find any results for "${search.q}"`} />
          )}
        </div>
      </div>
    </Body>
  );
};

export default ProductSearch;
